/* eslint-disable no-case-declarations */
import React, { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { BsSliders } from "react-icons/bs";
import { Modal } from "react-bootstrap";
import { addDays, differenceInDays, format, subDays } from "date-fns";
import { useSelector } from "react-redux";

import BoletoItem from "components/Boletos/Item";
import ContainedButton from "components/layout/form/containedButton";
import Loading from "components/layout/loading";
import ModalContent from "components/Boletos/ModalContent";
import ModalFilter from "components/Boletos/ModalFilter";
import ModalNothingContained from "components/Boletos/ModalNothingContained";
import Navbar from "components/MI/Navbar";
import OutlinedButton from "components/layout/form/outlinedButton";
import TextSelect from "components/layout/form/TextSelect";
import UserMenu from "components/layout/userMenuNew";
import api from "services/api";
import theme from "styles/theme";
import { normalizeBillStatus } from "utils/functions";
import { useTour } from "hooks/useTour";

import BillCompensationBanner from "components/BillCompensationBanner";
import ModalAutomaticDebit from "pages/ModalAutomaticDebit";
import Pagination from "components/layout/Pagination";
import TourBalloon from "components/NewTour/TourBallon";
import {
  BillsContentWrap,
  Body,
  BoxFilters,
  BoxPaddingContainer,
  Container,
  Content,
  ContentButtons,
  Divider,
  FilterWrap,
  FiltersAndActionsWrapper,
  Header,
  HeaderInfos,
  HeaderTitle,
  NotContent,
  StyledModal,
} from "./styles";

const Boletos = (): JSX.Element => {
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilter, setIsFilter] = useState(false);
  const [isShowModalContent, setIsShowModalContent] = useState(false);
  const [isShowModalFilter, setIsShowModalFilter] = useState(false);
  const [isShowModalNothingContained, setIsShowModalNothingContained] =
    useState(false);
  const [isShowModalAutomaticDebit, setIsShowModalAutomaticDebit] =
    useState(false);
  const [modalPayment, setModalPayment] = useState(null);
  const [modalInstallments, setModalInstallments] = useState(null);
  const [status, setStatus] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("recentToOld");
  const [totaPages, setTotalPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [overdueCount, setOverdueCount] = useState(0);

  const { selectedUnit } = useSelector((state) => ({
    // @ts-ignore
    selectedUnit: state.selectUnitReducer.selectedUnit,
  }));

  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  const getBills = async (page = 0): Promise<void> => {
    try {
      const accessToken = localStorage.getItem(
        "@UltragazEmpresarial:accessToken"
      );
      const document = localStorage.getItem("@UltragazEmpresarial:document");

      if (!accessToken || !document) {
        throw new Error("Token de acesso ou documento não encontrado.");
      }

      const headers = {
        access_token: accessToken,
      };

      const { search } = window.location;
      const getParams = new URLSearchParams(search);
      const beginDate = getParams.get("begin-date");
      const endDate = getParams.get("end-date");
      const filterStatus = getParams.get("status") || status;
      const order = getParams.get("order");
      const invoiceNumber = getParams.get("invoice-number");

      if (!!beginDate || !!endDate || !!filterStatus || !!invoiceNumber) {
        setIsFilter(true);
      }

      const params: any = {
        document,
        "invoice-number": invoiceNumber,
        "begin-due-date": beginDate,
        "end-due-date": endDate,
        "unit-id": selectedUnit?.instanceId,
        _offset: page ? page - 1 : 0,
        _limit: 10,
        _expand: "installment",
        _order_by: order,
      };

      if (filterStatus) {
        if (filterStatus.toLocaleLowerCase() !== "todos")
          params.status = filterStatus;
      }

      const res = await api.get("industrial/bill/v1/bills", {
        headers,
        params,
      });

      if (!res.data) {
        setIsLoading(false);
      }

      const pageTotal = Number(res.headers["total-pages"]);
      setTotalPages(pageTotal);

      organizeBankSlips(res.data);
    } catch (error) {
      setIsLoading(false);
      console.error("Erro ao buscar boletos:", error);
    }
  };

  const organizeBankSlips = async (response): Promise<void> => {
    let count = 0;

    setBills(
      response?.map((item) => {
        const normalizedStatus = normalizeBillStatus(
          item.status,
          item.totalAmount
        );

        if (selectedUnit?.hasDirectDebit && normalizedStatus === "VENCIDO") {
          const dueDate = new Date(item.dueDate);
          const currentDate = new Date();

          const diffInTime = currentDate.getTime() - dueDate.getTime();
          const diffInDays = diffInTime / (1000 * 3600 * 24);

          if (diffInDays > 7) {
            count += 1;
          }
        }

        const newValue = { invoice: item, installment: item };
        newValue.installment.paymentTypeBoleto = true;
        newValue.invoice.status = normalizedStatus;
        newValue.installment.status = normalizedStatus;
        return newValue;
      })
    );

    setOverdueCount(count);

    if (overdueCount > 0) {
      setIsShowModalAutomaticDebit(true);
    }
  };

  const getParams = (): void => {
    if (window) {
      const { search } = window.location;
      const params = new URLSearchParams(search);

      const statusParams = params.get("status");
      const beginDate = params.get("begin-date");
      const endDate = params.get("end-date");
      const selectedPeriodParams = params.get("selected-period");

      if (statusParams) {
        setStatus(statusParams);
      }

      if (selectedPeriodParams) {
        setSelectedPeriod(selectedPeriodParams);
      }

      if (beginDate && endDate && !selectedPeriodParams) {
        const beginFns = addDays(new Date(beginDate), 1);
        const endFns = addDays(new Date(endDate), 1);

        const daysDifference = differenceInDays(endFns, beginFns);

        switch (daysDifference) {
          case 90:
            setSelectedPeriod("old90days");
            break;
          case 60:
            setSelectedPeriod("old60days");
            break;
          case 30:
            setSelectedPeriod("old30days");
            break;
          case 15:
            setSelectedPeriod("old15days");
            break;
          default:
            break;
        }
      }
    }
  };

  const handleSelectedPeriod = (e: any): void => {
    const { value } = e.currentTarget;

    if (value === "old15days") {
      const beginDate = format(subDays(new Date(), 15), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");

      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${
        status ? `&status=${status}` : ""
      }&selected-period=${value}`;
    } else if (value === "old30days") {
      const beginDate = format(subDays(new Date(), 30), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");

      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${
        status ? `&status=${status}` : ""
      }&selected-period=${value}`;
    } else if (value === "old60days") {
      const beginDate = format(subDays(new Date(), 60), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");

      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${
        status ? `&status=${status}` : ""
      }&selected-period=${value}`;
    } else if (value === "old90days") {
      const beginDate = format(subDays(new Date(), 90), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");

      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${
        status ? `&status=${status}` : ""
      }&selected-period=${value}`;
    } else if (value === "recentToOld") {
      window.location.href = `/consulta-debitos?selected-period=recentToOld${
        status ? `&status=${status}` : ""
      }`;
    } else if (value === "oldToRecent") {
      window.location.href = `/consulta-debitos?order=dueDate${
        status ? `&status=${status}` : ""
      }&selected-period=oldToRecent`;
    }
  };

  const handleStatus = (e: any): void => {
    const { value } = e.currentTarget;
    const { search } = window.location;
    const params = new URLSearchParams(search);

    const statusParams = params.get("status");

    if (statusParams) {
      window.location.href = search.replace(statusParams, value);
      return;
    }

    if (search) {
      window.location.href = `${search}&status=${value}`;
      return;
    }

    window.location.href = `?status=${value}`;
  };

  useEffect(() => {
    getParams();

    (async () => {
      await getBills(selectedPage);
      setIsLoading(false);
    })();
  }, [selectedPage, totaPages]);

  const handleShow = (payment: any, installments: any): void => {
    setModalPayment(payment);
    setModalInstallments(installments);
    setIsShowModalContent(true);
  };

  useEffect(() => {
    setTourType("consulta-debitos");
    setSteps(3);
  }, [showTour, setTourType, setSteps]);

  return (
    <Navbar selected="consulta-debitos" openSubMenu>
      <UserMenu intro />
      <Container>
        <Header>
          <HeaderInfos>
            <HeaderTitle>Histórico de boletos</HeaderTitle>
            <BillCompensationBanner />
          </HeaderInfos>
        </Header>
        <Body>
          <FiltersAndActionsWrapper>
            <FilterWrap
              showTour={
                tourStep === 2 && tourType === "consulta-debitos" && showTour
              }
            >
              <BoxPaddingContainer>
                <BoxFilters>
                  <TextSelect
                    label="Ordenar por"
                    name="selectedPeriod"
                    type="text"
                    value={selectedPeriod}
                    onChange={handleSelectedPeriod}
                  >
                    <option key="recentToOld" value="recentToOld">
                      Mais recente para o mais antigo
                    </option>
                    <option key="oldToRecent" value="oldToRecent">
                      Mais antigo para o mais recente
                    </option>
                    <option key="old15days" value="old15days">
                      Últimos 15 dias
                    </option>
                    <option key="old30days" value="old30days">
                      Últimos 30 dias
                    </option>
                    <option key="old60days" value="old60days">
                      Últimos 60 dias
                    </option>
                    <option key="old90days" value="old90days">
                      Últimos 90 dias
                    </option>
                  </TextSelect>
                  <TextSelect
                    label="Situação"
                    name="status"
                    type="text"
                    value={status}
                    onChange={handleStatus}
                  >
                    <option key="todos" value="TODOS">
                      Todos
                    </option>
                    <option key="vencido" value="VENCIDO">
                      Vencido
                    </option>
                    <option key="pago" value="PAGO">
                      Pago
                    </option>
                  </TextSelect>
                </BoxFilters>
              </BoxPaddingContainer>
              {showTour &&
                tourStep === 2 &&
                tourType === "consulta-debitos" && (
                  <TourBalloon
                    title="Filtros"
                    content="Aqui você pode filtrar sua consulta."
                    position={{
                      left: "50%",
                      top: "48px",
                      transform: "translateX(-50%)",
                      arrowPosition: "top",
                      arrowTop: "-27px",
                      maxWidth: 320,
                      boxShadow: true,
                    }}
                  />
                )}
            </FilterWrap>
            <ContentButtons>
              <BillsContentWrap
                showTour={
                  tourStep === 1 && tourType === "consulta-debitos" && showTour
                }
              >
                <OutlinedButton
                  onClick={() => setIsShowModalNothingContained(true)}
                >
                  Solicitar carta de quitação
                </OutlinedButton>
                {showTour &&
                  tourStep === 1 &&
                  tourType === "consulta-debitos" && (
                    <TourBalloon
                      title="Carta de quitação"
                      content="Solicite aqui sua carta de quitação de débitos."
                      position={{
                        top: "48px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        arrowPosition: "top",
                        maxWidth: 320,
                        arrowTop: "-30%",
                        boxShadow: true,
                      }}
                    />
                  )}
              </BillsContentWrap>
              <ContainedButton
                LeadingIcon={BsSliders}
                propsLeadingIcon={{
                  color: theme.colors.white,
                  size: 24,
                }}
                onClick={() => setIsShowModalFilter(true)}
                disabled={isLoading}
              >
                Filtros
              </ContainedButton>
              {isFilter && (
                <OutlinedButton
                  LeadingIcon={AiOutlineClose}
                  propsLeadingIcon={{
                    color: theme.colors.blue,
                    size: 24,
                  }}
                  onClick={() => {
                    window.location.href = "/consulta-debitos";
                  }}
                  disabled={isLoading}
                >
                  Limpar
                </OutlinedButton>
              )}
            </ContentButtons>
          </FiltersAndActionsWrapper>
          <BillsContentWrap
            showTour={
              tourStep === 3 && tourType === "consulta-debitos" && showTour
            }
          >
            {bills?.length > 0 ? (
              <Content>
                {bills?.map((bill, index) => (
                  <React.Fragment key={bill}>
                    {index !== 0 && <Divider />}
                    <BoletoItem
                      installment={bill.installment}
                      invoice={bill.invoice}
                      open={() => handleShow(bill.invoice, bill.installment)}
                    />
                  </React.Fragment>
                ))}
              </Content>
            ) : (
              <NotContent>
                {isFilter ? (
                  <span>Você não possui boletos para esse filtro!</span>
                ) : (
                  <>
                    <span>Você não possui boletos!</span>
                    <span>
                      {
                        "Caso tenha se cadastrado recentemente, a primeira cobrança individualizada, em seu nome, será processada em até "
                      }
                      <span>60 dias</span>.
                    </span>
                  </>
                )}
              </NotContent>
            )}
            {showTour && tourStep === 3 && tourType === "consulta-debitos" && (
              <TourBalloon
                title="Boletos"
                content="Aqui você encontra todos os seus boletos e você poderá baixar, imprimir ou enviar por e-mail."
                position={{
                  left: "56%",
                  top: "0",
                  arrowPosition: "unset",
                  maxWidth: 320,
                  arrowLeft: "-10%",
                  boxShadow: true,
                }}
              />
            )}
          </BillsContentWrap>
          {totaPages > 1 && (
            <Pagination
              totalPages={totaPages}
              selectedPage={selectedPage}
              onPageChange={setSelectedPage}
              isLoading={isLoading}
            />
          )}
        </Body>
      </Container>
      <Modal
        show={isShowModalContent}
        onHide={() => setIsShowModalContent(false)}
        size="lg"
        dialogClassName="modal-90w"
        aria-labelledby="example-custom-modal-styling-title"
        animation={false}
        centered
      >
        <ModalContent
          // @ts-ignore
          close={() => setIsShowModalContent(false)}
          payment={modalPayment}
          installment={modalInstallments}
        />
      </Modal>
      <StyledModal
        show={isShowModalFilter}
        onHide={() => setIsShowModalFilter(false)}
        aria-labelledby="example-custom-modal-styling-title"
        animation={false}
        centered
      >
        <ModalFilter close={() => setIsShowModalFilter(false)} />
      </StyledModal>
      <ModalNothingContained
        show={isShowModalNothingContained}
        onClose={() => setIsShowModalNothingContained(false)}
      />
      <ModalAutomaticDebit
        show={isShowModalAutomaticDebit}
        onClose={() => setIsShowModalAutomaticDebit(false)}
      />
      {isLoading && <Loading />}
    </Navbar>
  );
};

export default Boletos;
